// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectionActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { compose, withProps, withHandlers } from 'recompose';
import ConnectionStore from "logic/Izenda.ConnectionStore";
import ConnectionDetailStore from "logic/Izenda.ConnectionDetailStore";
import withNavigator from 'share/components/hoc/Izenda.WithNavigator';
import constants from 'logic/data/Izenda.ConnectionConstants';
import localizer from 'share/localization/Izenda.Localizer';

const { curry, isEmpty, getOr } = require('lodash/fp');

const actions = compose(
  withProps(() => {
    const masterStore = new ConnectionStore();
    const detailStore = new ConnectionDetailStore(masterStore);
    return {
      masterStore: masterStore,
      detailStore: detailStore,
      originalOnPageHide: window.onpagehide
    }
  }),
  withNavigator({
    onpagehide: registerOnPageHideHandler
  }),
  withHandlers({
    testConnection: curry(testConnection),
    connectToDataSource: curry(connectToDataSource)
  })
);

/**
 * Connect to data source
 * @param {any} props
 * @param {ConnectionDetailStore} detailStore
 * @returns {Promise}
 */
function connectToDataSource(props, detailStore) {
  const { loader, confirmFn } = props;
  const connectionDetail = detailStore.item;

  if (!connectionDetail.isConnectionStringReady) {
    return Promise.resolve(false);
  }

  const connectToDataSource = connectionDetail.isCurrentConnectionStringSameAsSavedConnectionString
    ? detailStore.connectToDataSource(connectionDetail, true)
    : confirmFn('Confirmation', { description: constants.NEW_CONNECTIONSTRING_DIFFERENT_SCHEMA })
      .then(detailStore.connectToDataSource.bind(detailStore, connectionDetail, true));

  loader.startLoading();
  return connectToDataSource
    .finally(() => {
      loader.endLoading();
      if (!connectionDetail.canSave) {
        connectionDetail.markDirty();
      }
    });
}

/**
 * Test connection
 * @param {any} props
 * @param {ConnectionDetailStore} detailStore
 * @returns {Promise}
 */
function testConnection(props, detailStore) {
  const { loader, confirmMessage, validation } = props;
  const connectionDetail = detailStore.item;

  loader.startLoading();
  return validation
    .validate('connectionString')
    .then(success => {
      if (success) {
        detailStore.populateDatabaseName().then(() => {
        });
        return success;
      } else if (!connectionDetail.isNew) {
        return confirmMessage('info', constants.REVERSE_CONNECTION_STRING).then(() => {
          validation.reset('connectionString');
          detailStore.load(connectionDetail.id).then(() => {
          });
          return null;
        });
      }
      return null;
    })
    .then(validationStatus =>
      connectionDetail.isCurrentConnectionStringSameAsSavedConnectionString
        ? validationStatus
        : detailStore.validateSchema())
    .catch(() => {
    })
    .finally(loader.endLoading.bind(loader));
}

/**
 * Custom onPageHide handler.
 */
function registerOnPageHideHandler() {
  const { detailStore } = this.props;
  return () => {
    detailStore.masterStore.deleteTempExcelFiles();
  };
}

export default actions;



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/Connection/Izenda.ConnectionActions.js